import React, { useEffect, useState, useMemo, useContext } from "react";
import { useNavigate } from "react-router";
import {
  Table,
  Button,
  Input,
  Space,
  Breadcrumb,
  Typography,
  Card,
  Modal,
  Form,
  message,
} from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { update_agent_special_id } from "../../api/AgentApi";
import { list_agents } from "../../api/AgentApi";
import { GlobalContext } from "../../App";

const { Title } = Typography;

const AgentsList = () => {
  const navigate = useNavigate();
  const { setToastOpen, setToastMessage, setToastSeverity, languages } =
    useContext(GlobalContext);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [agents, setAgents] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [form] = Form.useForm();
  const getData = async () => {
    try {
      setLoading(true);
      const response = await list_agents();

      // Ensure response is an array
      const agentsData = Array.isArray(response.data)
        ? response.data
        : [];

      setAllAgents(agentsData);
      setAgents(agentsData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching agents:", error);
      setAllAgents([]);
      setAgents([]);
      setLoading(false);
    }
  };
  const onSearch = (value) => {
    setSearchText(value);

    if (!value.trim()) {
      setAgents(allAgents);
      return;
    }

    const filteredData = allAgents.filter((agent) => {
      return (
        agent.agent_first_name
          ?.toLowerCase()
          .includes(value.toLowerCase()) ||
        agent.agent_last_name
          ?.toLowerCase()
          .includes(value.toLowerCase()) ||
        agent.agent_special_id
          ?.toLowerCase()
          .includes(value.toLowerCase())
      );
    });

    setAgents(filteredData);
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleTableChange = (newPagination, filters, sorter) => {
    setPagination(newPagination);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const columns = [
    {
      title: "Agent Name",
      dataIndex: "agent_display_name",
      key: "agent_display_name",
      render: (text, record) => (
        <span>
          {record.agent_first_name + " " + record.agent_last_name}
        </span>
      ),
      sorter: (a, b) =>
        a.agent_first_name?.localeCompare(b.agent_first_name || ""),
    },

    {
      title: "Agent ID",
      dataIndex: "agent_special_id",
      key: "agent_special_id",
      render: (text, record) => {
        if (!text) {
          return (
            <span
              className="text-xs text-red-500 font-bold cursor-pointer hover:underline"
              onClick={() => handleEdit(record)}
            >
              Add agent ID
            </span>
          );
        } else {
          return <span>{text}</span>;
        }
      },
    },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   render: (_, record) => (
    //     <Space>
    //       <Button
    //         type="link"
    //         icon={<EditOutlined />}
    //         onClick={() =>
    //           navigate(`/admin/agents/edit/${record.agent_id}`)
    //         }
    //       >
    //         Edit
    //       </Button>
    //     </Space>
    //   ),
    // },
  ];

  const handleEdit = (record) => {
    setCurrentAgent(record);
    form.setFieldsValue({
      agent_special_id: record.agent_special_id || "",
    });
    setIsEditModalVisible(true);
  };

  const handleEditCancel = () => {
    setIsEditModalVisible(false);
    setCurrentAgent(null);
    form.resetFields();
  };

  const handleEditSubmit = async () => {
    try {
      let response = await update_agent_special_id(
        currentAgent.agent_id,
        form.getFieldValue("agent_special_id")
      );
      setIsEditModalVisible(false);
      setAgents((prev) =>
        prev.map((agent) =>
          agent.agent_id === currentAgent.agent_id
            ? {
                ...agent,
                agent_special_id: form.getFieldValue(
                  "agent_special_id"
                ),
              }
            : agent
        )
      );
      setToastOpen(true);
      setToastMessage("Agent ID updated successfully");
      setToastSeverity("success");
    } catch (error) {
      console.error("Error updating agent ID:", error);
      setToastOpen(true);
      setToastMessage(error.response.data.message);
      setToastSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <div className="p-0">
      <Breadcrumb className="mb-6">
        <Breadcrumb.Item>Admin</Breadcrumb.Item>
        <Breadcrumb.Item>Agents</Breadcrumb.Item>
      </Breadcrumb>

      <div className="flex justify-between items-center mb-6">
        <Title level={3} className="mb-0">
          Agents
        </Title>
        
      </div>

      <Card className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="w-1/3">
            <Input
              placeholder="Search agents..."
              prefix={<SearchOutlined />}
              onChange={(e) => onSearch(e.target.value)}
              allowClear
            />
          </div>
          <div>
            <span style={{ marginLeft: 8 }}>
              {selectedRowKeys.length > 0
                ? `Selected ${selectedRowKeys.length} items`
                : ""}
            </span>
          </div>
        </div>

        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={agents}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: "max-content" }}
          rowKey="agent_id"
          size="small"
        />
      </Card>

      {/* Edit Agent ID Modal */}
      <Modal
        title="Edit Agent ID"
        open={isEditModalVisible}
        onCancel={handleEditCancel}
        onOk={handleEditSubmit}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="agent_special_id"
            label="Agent ID"
            rules={[
              { required: true, message: "Please enter the agent ID" },
            ]}
          >
            <Input placeholder="Enter agent ID" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AgentsList;
